/**
 * Générateur — Automatisme : mobiliser une procédure adaptée (linéarité pour
 * la multiplication ou l'addition, retour à l'unité) pour résoudre un problème
 * lié à la proportionnalité.
 * Attendu ID : 1085 | Niveau : 5ème | Domaine : Proportionnalité
 * Rubrique : AUTOMATISME (programme 2025) — mobilisation en rituel court.
 * Labels (cartes exerciseur) :
 *   - Double, triple, moitié      (saisie, linéarité multiplicative)
 *   - Assembler deux quantités    (saisie, linéarité additive)
 *   - Retour à l'unité            (saisie, deux gestes triviaux)
 *   - Choisir le bon geste        (QCM, cadence raisonnement)
 *
 * ── Plan de cartes validé (2026-09-01) ─────────────────────────────────────
 * Source : Automatisme n°47 du doc 5e + grille P1-P12 (analyse BO cycles 3-4).
 * LIMITE DE PÉRIMÈTRE (fiche n°47) : le produit en croix n'est pas enseigné
 * et le coefficient de proportionnalité n'est pas nommé — les SEULES
 * procédures mobilisables sont la linéarité (multiplicative, additive) et le
 * retour à l'unité. PROSCRIPTIONS (P7) : produit en croix nulle part, même en
 * distracteur (aucune expression « q × m ÷ n ») ; « coefficient » jamais
 * écrit ; aucune lettre.
 * INVARIANTS :
 *   - contextes = GRANDEURS uniquement (recette, prix au kilo, objets
 *     identiques, distance à allure constante), jamais de suite abstraite ;
 *   - réponses de SAISIE toujours ENTIÈRES (g, €, km) : pas d'ambiguïté
 *     d'écriture décimale dans le correcteur libre ; les données restent
 *     dans le champ P3 (petits entiers, valeurs unitaires simples) ;
 *   - chaque tirage est AUTO-VÉRIFIÉ (entier, positif, cible ≠ base,
 *     vraisemblance des allures) — throw plutôt qu'émettre un énoncé faux ;
 *   - QCM procédure : le PIÈGE ADDITIF (obstacle P11, question g. de la
 *     fiche : « une personne de plus → un gramme de plus ») est présent à
 *     CHAQUE tirage et n'est JAMAIS correct — recompté numériquement, throw
 *     si un distracteur vaut la bonne réponse ;
 *   - contenu.cadence = 'raisonnement' sur le QCM procédure (convention
 *     gen_1039, métadonnée dormante).
 */
import {
    randomChoice, shuffle,
    makeQcm, makeSaisie,
} from '../../../utils';

const ATTENDU_ID = 1085;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_MULT = 'Double, triple, moitié';
const L_ADD = 'Assembler deux quantités';
const L_UNITE = 'Retour à l\'unité';
const L_PROCEDURE = 'Choisir le bon geste';

// ─── Le domaine : contextes de grandeurs ───────────────────────────────────
// Valeurs unitaires ENTIÈRES par contexte : tout résultat (base, cible) reste
// un entier, quel que soit le geste demandé.

const INGREDIENTS = ['farine', 'sucre', 'riz'];
const FRUITS = ['tomates', 'pommes', 'poires'];
const OBJETS_1085 = ['classeurs identiques', 'bouteilles de jus identiques', 'boîtes de crayons identiques'];
const PRENOMS = [{ nom: 'Léa', pr: 'elle' }, { nom: 'Tom', pr: 'il' }];

const U_RECETTE = [25, 30, 40, 50, 60]; // g par personne
const U_PRIX = [2, 3];                  // € par kg ou par objet

/** Throw si une valeur n'est pas un entier strictement positif. */
const verifierEntiers = (...valeurs) => {
    valeurs.forEach((v) => {
        if (!Number.isInteger(v) || v <= 0) {
            throw new Error('gen_1085 : valeur non entière ou nulle — tirage refusé');
        }
    });
};

/**
 * Tire une base (n, q) dans un contexte, avec énoncé et question.
 * `u` reste interne (valeur unitaire), jamais montré : le retour à l'unité
 * est un GESTE de l'élève, pas une donnée.
 */
const tirerBase = (ctx) => {
    if (ctx === 'recette') {
        const n = randomChoice([2, 3, 4, 6]);
        const u = randomChoice(U_RECETTE);
        const ing = randomChoice(INGREDIENTS);
        return {
            ctx, n, u, q: n * u, suffixe: 'g',
            enonce: `Une recette pour ${n} personnes demande ${n * u} g de ${ing}.`,
            question: (m) => `Quelle quantité de ${ing} faut-il pour ${m} personnes ?`,
            calcul: (m) => `Quel calcul donne la quantité de ${ing} pour ${m} personnes ?`,
        };
    }
    if (ctx === 'prix') {
        const n = randomChoice([2, 3, 4, 5, 6]);
        const u = randomChoice(U_PRIX);
        const fruit = randomChoice(FRUITS);
        return {
            ctx, n, u, q: n * u, suffixe: '€',
            enonce: `${n} kg de ${fruit} coûtent ${n * u} €.`,
            question: (m) => `Combien coûtent ${m} kg de ${fruit} ?`,
            calcul: (m) => `Quel calcul donne le prix de ${m} kg ?`,
        };
    }
    if (ctx === 'objets') {
        const n = randomChoice([2, 3, 4, 5, 6]);
        const u = randomChoice(U_PRIX);
        const obj = randomChoice(OBJETS_1085);
        return {
            ctx, n, u, q: n * u, suffixe: '€',
            enonce: `${n} ${obj} coûtent ${n * u} €.`,
            question: (m) => `Combien coûtent ${m} de ces ${obj.split(' ')[0]} ?`,
            calcul: (m) => `Quel calcul donne le prix de ${m} de ces ${obj.split(' ')[0]} ?`,
        };
    }
    // distance : la base est (t minutes → d km), vraisemblance d'allure gardée
    // entre 6 et 30 km/h (marche rapide → vélo) — throw jamais : on retire.
    for (let essai = 0; essai < 30; essai++) {
        const t = randomChoice([10, 15, 20, 30]);
        const d = randomChoice([2, 3, 4, 6]);
        const vitesse = (60 * d) / t;
        if (vitesse < 6 || vitesse > 30) continue;
        const p = randomChoice(PRENOMS);
        return {
            ctx, n: t, u: null, q: d, suffixe: 'km',
            enonce: `À allure constante, ${p.nom} parcourt ${d} km en ${t} minutes.`,
            question: (m) => `Quelle distance parcourt-${p.pr} en ${m} minutes ?`,
            calcul: (m) => `Quel calcul donne la distance parcourue en ${m} minutes ?`,
        };
    }
    throw new Error('gen_1085 : aucune allure vraisemblable — tirage refusé');
};

// ─── Format 1 : linéarité multiplicative (un geste) ────────────────────────
// ×2, ×3, ×4, ×10 ou la moitié. ×10 réservé aux contextes où la cible reste
// vraisemblable (grande tablée, commande groupée) ; la moitié exige une base
// paire ET une cible ≥ 2 (la cible 1 relève du retour à l'unité).

let derniereSignatureMult1085 = null;

const genLineariteMult = () => {
    let payload = construireLineariteMult();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureMult1085; t++) {
        payload = construireLineariteMult();
    }
    derniereSignatureMult1085 = payload.texte;
    return payload;
};

const construireLineariteMult = () => {
    for (let essai = 0; essai < 40; essai++) {
        const ctx = randomChoice(['recette', 'prix', 'objets', 'distance']);
        const base = tirerBase(ctx);
        const ksAutorises = ctx === 'recette' || ctx === 'objets' ? [2, 3, 4, 10, 0.5] : [2, 3, 4, 0.5];
        const k = randomChoice(ksAutorises);

        if (k === 0.5 && (base.n % 2 !== 0 || base.q % 2 !== 0 || base.n / 2 < 2)) continue;
        const m = k === 0.5 ? base.n / 2 : base.n * k;
        const reponse = k === 0.5 ? base.q / 2 : base.q * k;
        if (ctx === 'distance' && m > 90) continue; // au-delà, la durée décroche du rituel
        if (m === base.n) continue;

        verifierEntiers(m, reponse);
        const texte = `<p>${base.enonce}</p><p>${base.question(m)}</p>`;
        const payload = makeSaisie(String(reponse), texte, { ...opts(L_MULT), suffixe: base.suffixe });
        payload._meta.params = { ctx, k, n: base.n, m, q: base.q, reponse };
        return payload;
    }
    throw new Error('gen_1085 : linéarité multiplicative introuvable après 40 essais');
};

// ─── Format 2 : linéarité additive (les deux valeurs sont DONNÉES) ─────────
// La fiche : « 6 = 4 + 2, donc 200 + 100 ». La seconde ligne est toujours la
// moitié de la première (parités garanties), la cible est leur somme — le
// geste attendu est UNE addition, choisie parce que la cible se décompose.
// LIGNE INTRUSE (revue 2026-09-01, A2, décision prof) : une 3e ligne cohérente
// avec la situation est donnée, mais AUCUNE paire la contenant n'atteint la
// cible (ni elle seule, ni son double) — « j'additionne deux nombres au
// hasard » échoue, l'élève doit CHOISIR sa décomposition. Les trois lignes
// sont affichées dans un ordre mélangé (pas d'heuristique de position), et
// n3 est tantôt sous n1, tantôt au-dessus (pas d'heuristique « les deux plus
// grands »). Toute procédure mathématiquement juste qui utilise la ligne
// intruse (retour à l'unité...) donne la même réponse : on ne piège que
// l'addition aveugle.

let derniereSignatureAdd1085 = null;

const genLineariteAdd = () => {
    let payload = construireLineariteAdd();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureAdd1085; t++) {
        payload = construireLineariteAdd();
    }
    derniereSignatureAdd1085 = payload.texte;
    return payload;
};

const construireLineariteAdd = () => {
    const ctx = randomChoice(['recette', 'prix', 'objets', 'distance']);

    if (ctx === 'distance') {
        for (let essai = 0; essai < 30; essai++) {
            const t1 = randomChoice([10, 20, 30]);
            const d1 = randomChoice([2, 4, 6]);
            const vitesse = (60 * d1) / t1;
            if (vitesse < 6 || vitesse > 30) continue;
            const t2 = t1 / 2; const d2 = d1 / 2;
            const m = t1 + t2; const reponse = d1 + d2;
            // Ligne intruse : même allure, distance entière, aucune paire vers m.
            const candidatsT3 = [];
            for (let t3c = 5; t3c <= t1 + 20; t3c += 5) {
                if ((t3c * d1) % t1 !== 0) continue;
                if (t3c === t1 || t3c === t2 || t3c === m || 2 * t3c === m) continue;
                if (t3c + t1 === m || t3c + t2 === m) continue;
                candidatsT3.push(t3c);
            }
            if (candidatsT3.length === 0) continue;
            const t3 = randomChoice(candidatsT3);
            const d3 = (t3 * d1) / t1;
            verifierEntiers(t2, d2, t3, d3, m, reponse);
            const p = randomChoice(PRENOMS);
            const lignes = shuffle([[d1, t1], [d2, t2], [d3, t3]]);
            const detail = lignes.map(([dk, tk]) => `${dk} km en ${tk} minutes`).join(', ');
            const texte = `<p>À allure constante, ${p.nom} parcourt ${detail}.</p><p>Quelle distance parcourt-${p.pr} en ${m} minutes ?</p>`;
            const payload = makeSaisie(String(reponse), texte, { ...opts(L_ADD), suffixe: 'km' });
            payload._meta.params = { ctx, n1: t1, n2: t2, n3: t3, m, q1: d1, q2: d2, q3: d3, reponse };
            return payload;
        }
        throw new Error('gen_1085 : allure additive introuvable — tirage refusé');
    }

    const n1 = randomChoice([4, 6, 8, 10, 12]);
    const n2 = n1 / 2;
    const m = n1 + n2;
    // Ligne intruse : aucune paire la contenant n'atteint m (cf. bloc de tête).
    const candidatsN3 = [];
    for (let c = 2; c <= n1 + 4; c++) {
        if (c === n1 || c === n2 || c === m || 2 * c === m) continue;
        if (c + n1 === m || c + n2 === m) continue;
        candidatsN3.push(c);
    }
    const n3 = randomChoice(candidatsN3);
    let texte; let q1; let q2; let q3; let suffixe;
    if (ctx === 'recette') {
        const u = randomChoice(U_RECETTE);
        const ing = randomChoice(INGREDIENTS);
        q1 = n1 * u; q2 = q1 / 2; q3 = n3 * u; suffixe = 'g';
        const lignes = shuffle([[n1, q1], [n2, q2], [n3, q3]]);
        texte = `<p>Pour ${lignes[0][0]} personnes, il faut ${lignes[0][1]} g de ${ing} ; pour ${lignes[1][0]} personnes, ${lignes[1][1]} g ; pour ${lignes[2][0]} personnes, ${lignes[2][1]} g.</p><p>Quelle quantité de ${ing} faut-il pour ${m} personnes ?</p>`;
    } else if (ctx === 'prix') {
        const u = randomChoice(U_PRIX);
        const fruit = randomChoice(FRUITS);
        q1 = n1 * u; q2 = q1 / 2; q3 = n3 * u; suffixe = '€';
        const lignes = shuffle([[n1, q1], [n2, q2], [n3, q3]]);
        texte = `<p>${lignes[0][0]} kg de ${fruit} coûtent ${lignes[0][1]} € ; ${lignes[1][0]} kg coûtent ${lignes[1][1]} € ; ${lignes[2][0]} kg coûtent ${lignes[2][1]} €.</p><p>Combien coûtent ${m} kg de ${fruit} ?</p>`;
    } else {
        const u = randomChoice(U_PRIX);
        const obj = randomChoice(OBJETS_1085);
        const court = obj.split(' ')[0];
        q1 = n1 * u; q2 = q1 / 2; q3 = n3 * u; suffixe = '€';
        const lignes = shuffle([[n1, q1], [n2, q2], [n3, q3]]);
        texte = `<p>${lignes[0][0]} ${obj} coûtent ${lignes[0][1]} € ; ${lignes[1][0]} de ces ${court} coûtent ${lignes[1][1]} € ; ${lignes[2][0]} de ces ${court} coûtent ${lignes[2][1]} €.</p><p>Combien coûtent ${m} de ces ${court} ?</p>`;
    }
    const reponse = q1 + q2;
    if (n1 + n2 !== m || q2 * 2 !== q1) throw new Error('gen_1085 : décomposition additive incohérente');
    if (n1 + n3 === m || n2 + n3 === m || n3 === m || 2 * n3 === m) {
        throw new Error('gen_1085 : la ligne intruse atteint la cible');
    }
    verifierEntiers(n2, n3, m, q1, q2, q3, reponse);
    const payload = makeSaisie(String(reponse), texte, { ...opts(L_ADD), suffixe });
    payload._meta.params = { ctx, n1, n2, n3, m, q1, q2, q3, reponse };
    return payload;
};

// ─── Format 3 : retour à l'unité (deux gestes triviaux) ────────────────────
// La cible n'est JAMAIS un multiple de la base (m % n ≠ 0) : la linéarité
// seule ne suffit pas, il FAUT passer par la valeur d'une unité — qui est
// entière par construction. Distance exclue (la valeur « au km par minute »
// n'est pas un objet de 5e).

const COUPLES_UNITE = [[3, 5], [3, 7], [4, 7], [6, 7], [4, 9], [2, 5]];

let derniereSignatureUnite1085 = null;

const genRetourUnite = () => {
    let payload = construireRetourUnite();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureUnite1085; t++) {
        payload = construireRetourUnite();
    }
    derniereSignatureUnite1085 = payload.texte;
    return payload;
};

const construireRetourUnite = () => {
    const [n, m] = randomChoice(COUPLES_UNITE);
    const ctx = randomChoice(['recette', 'prix', 'objets']);
    let u; let texte; let suffixe;
    if (ctx === 'recette') {
        u = randomChoice(U_RECETTE);
        const ing = randomChoice(INGREDIENTS);
        texte = `<p>Une recette pour ${n} personnes demande ${n * u} g de ${ing}.</p><p>Quelle quantité de ${ing} faut-il pour ${m} personnes ?</p>`;
        suffixe = 'g';
    } else if (ctx === 'prix') {
        u = randomChoice(U_PRIX);
        const fruit = randomChoice(FRUITS);
        texte = `<p>${n} kg de ${fruit} coûtent ${n * u} €.</p><p>Combien coûtent ${m} kg de ${fruit} ?</p>`;
        suffixe = '€';
    } else {
        u = randomChoice(U_PRIX);
        const obj = randomChoice(OBJETS_1085);
        texte = `<p>${n} ${obj} coûtent ${n * u} €.</p><p>Combien coûtent ${m} de ces ${obj.split(' ')[0]} ?</p>`;
        suffixe = '€';
    }
    if (m % n === 0 || m === n) throw new Error('gen_1085 : couple unité dégénéré (cible multiple de la base)');
    const reponse = m * u;
    verifierEntiers(u, reponse);
    const payload = makeSaisie(String(reponse), texte, { ...opts(L_UNITE), suffixe });
    payload._meta.params = { ctx, n, m, u, q: n * u, reponse };
    return payload;
};

// ─── Format 4 : QCM « quel calcul ? » (choisir la procédure) ───────────────
// On ne calcule pas, on RECONNAÎT le geste adapté. Trois sous-cas (multiple,
// moitié, retour à l'unité) ; le piège additif (P11 : « +1 personne → +1 g »)
// est présent à CHAQUE tirage. Toutes les options sont évaluées : exactement
// UNE vaut la bonne réponse, sinon throw. Distance exclue (expressions sur de
// trop petits nombres). Aucune expression « q × m ÷ n » (P7).

let derniereSignatureProcedure1085 = null;

const genProcedureQcm = () => {
    let payload = construireProcedureQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureProcedure1085; t++) {
        payload = construireProcedureQcm();
    }
    derniereSignatureProcedure1085 = payload.texte;
    return payload;
};

const construireProcedureQcm = () => {
    for (let essai = 0; essai < 40; essai++) {
        const sousCas = randomChoice(['mult', 'moitie', 'unite']);
        const ctx = randomChoice(['recette', 'prix', 'objets']);
        const base = tirerBase(ctx);
        const { n, q } = base;

        let m; let bonne; let reponse; let options;
        if (sousCas === 'mult') {
            const k = randomChoice([2, 3, 4]);
            m = n * k;
            reponse = q * k;
            bonne = `${q} × ${k}`;
            options = [
                { txt: bonne, val: q * k },
                { txt: `${q} + ${m - n}`, val: q + (m - n) },      // piège additif (P11)
                { txt: `${q} × ${m}`, val: q * m },
                { txt: `${q} ÷ ${k}`, val: q / k },
            ];
        } else if (sousCas === 'moitie') {
            if (n % 2 !== 0 || n < 4 || q % 2 !== 0) continue;
            m = n / 2;
            reponse = q / 2;
            bonne = `${q} ÷ 2`;
            options = [
                { txt: bonne, val: q / 2 },
                { txt: `${q} - ${n - m}`, val: q - (n - m) },      // piège additif (P11)
                { txt: `${q} × 2`, val: q * 2 },
                { txt: `${q} ÷ ${n}`, val: q / n },
            ];
        } else {
            const couple = randomChoice(COUPLES_UNITE.filter(([cn]) => cn === n));
            if (!couple) continue;
            m = couple[1];
            reponse = (q / n) * m;
            bonne = `${q} ÷ ${n} × ${m}`;
            options = [
                { txt: bonne, val: (q / n) * m },
                { txt: `${q} ÷ ${m} × ${n}`, val: (q / m) * n },   // gestes inversés
                { txt: `${q} + ${m - n}`, val: q + (m - n) },      // piège additif (P11)
                { txt: `${q} × ${m}`, val: q * m },
            ];
        }

        // Recomptage : exactement UNE option vaut la bonne réponse, textes uniques.
        const gagnantes = options.filter((o) => Math.abs(o.val - reponse) < 1e-9);
        if (gagnantes.length !== 1 || gagnantes[0].txt !== bonne) {
            throw new Error('gen_1085 : un distracteur du QCM procédure vaut la bonne réponse');
        }
        if (new Set(options.map((o) => o.txt)).size !== 4) continue;

        const texte = `<p>${base.enonce}</p><p>${base.calcul(m)}</p>`;
        const payload = makeQcm(options.map((o) => o.txt), bonne, texte, opts(L_PROCEDURE));
        // Métadonnée DORMANTE (convention gen_1039) : cadence de raisonnement.
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { ctx, sousCas, n, m, q, reponse };
        return payload;
    }
    throw new Error('gen_1085 : QCM procédure introuvable après 40 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    linearite_mult_saisie: genLineariteMult,
    linearite_add_saisie: genLineariteAdd,
    retour_unite_saisie: genRetourUnite,
    procedure_qcm_unique: genProcedureQcm,
};

const FORMAT_LABELS = { saisie: 'Saisie', qcm_unique: 'QCM' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'mult', label: L_MULT, formatsList: buildFormats('linearite_mult') },
    { id: 'add', label: L_ADD, formatsList: buildFormats('linearite_add') },
    { id: 'unite', label: L_UNITE, formatsList: buildFormats('retour_unite') },
    { id: 'procedure', label: L_PROCEDURE, formatsList: buildFormats('procedure') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
